// ============================================================================
// GESTIMMONY — Onboarding checklist (/app/onboarding)
// ============================================================================
// Guide interactif en 3 étapes pour activer le compte :
//   1. Créer un bien   → /app/biens
//   2. Ajouter un locataire → /app/locataires
//   3. Générer sa 1ʳᵉ quittance → /app/quittances
// Levier d'activation : les utilisateurs qui complètent l'onboarding
// churnent significativement moins.
// ============================================================================
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Check, Building2, Users, Receipt as ReceiptIcon, ArrowRight, Sparkles } from "lucide-react";

const STEPS = [
  {
    key: "has_property", title: "Créez votre premier bien",
    desc: "Ajoutez un appartement, une maison ou un local à votre parc locatif.",
    icon: Building2, cta: "Ajouter un bien", path: "/app/biens", testid: "onboarding-step-property",
  },
  {
    key: "has_tenant", title: "Ajoutez un locataire",
    desc: "Renseignez ses coordonnées et éventuellement un garant pour le rattacher à un bail.",
    icon: Users, cta: "Ajouter un locataire", path: "/app/locataires", testid: "onboarding-step-tenant",
  },
  {
    key: "has_receipt", title: "Générez votre 1ʳᵉ quittance",
    desc: "Une fois le bail créé, émettez la quittance PDF signée et prête à envoyer.",
    icon: ReceiptIcon, cta: "Créer une quittance", path: "/app/quittances", testid: "onboarding-step-receipt",
  },
];

export default function OnboardingPage() {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/onboarding/status");
        setStatus(data);
      } catch (e) { console.error(e); }
    })();
  }, []);

  if (!status) return <div className="text-sm text-slate-500">Chargement…</div>;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="onboarding-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-1.5 text-xs font-semibold bg-teal-100 text-teal-800 px-2 py-1 rounded-full mb-2">
            <Sparkles className="h-3 w-3" /> Bienvenue sur Gestimmony
          </div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">
            Activez votre compte en 3 étapes
          </h1>
          <p className="mt-1 text-slate-600">
            Suivez ces étapes pour prendre en main Gestimmony et générer votre première quittance en moins de 5 minutes.
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-3xl font-bold text-teal-700" data-testid="onboarding-progress-value">
            {status.progress_pct}%
          </div>
          <div className="text-xs text-slate-500">{status.steps_done} / {status.total_steps} étapes</div>
        </div>
      </div>

      <Progress value={status.progress_pct} className="h-2" data-testid="onboarding-progress-bar" />

      {status.complete && (
        <Card className="p-6 bg-emerald-50 border-emerald-200" data-testid="onboarding-complete-banner">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-emerald-500 flex items-center justify-center">
              <Check className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-emerald-900">Bravo, votre compte est activé !</h3>
              <p className="text-sm text-emerald-800">Vous maîtrisez les bases. Explorez les modules avancés : Finances, États des lieux, Incidents.</p>
            </div>
          </div>
        </Card>
      )}

      <div className="space-y-3">
        {STEPS.map((step, i) => {
          const done = status[step.key];
          const Icon = step.icon;
          return (
            <Card
              key={step.key}
              data-testid={step.testid}
              className={`p-5 flex items-center gap-4 transition ${
                done ? "bg-emerald-50/50 border-emerald-200" : "bg-white"
              }`}
            >
              <div className={`h-12 w-12 rounded-full flex items-center justify-center shrink-0 ${
                done ? "bg-emerald-500 text-white" : "bg-teal-100 text-teal-700"
              }`}>
                {done ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-400">ÉTAPE {i + 1}</span>
                  {done && (
                    <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                      Fait
                    </span>
                  )}
                </div>
                <h3 className="font-heading font-bold text-slate-900 mt-1">{step.title}</h3>
                <p className="text-sm text-slate-600 mt-1">{step.desc}</p>
              </div>
              {!done && (
                <Link to={step.path}>
                  <Button data-testid={`${step.testid}-cta`}>
                    {step.cta} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
